import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Tooltip from '../components/Tooltip';
import PatientDetailPanel from '../components/PatientDetailModal';
import TagBadge from '../components/TagBadge';
import TagFilterDropdown from '../components/TagFilterDropdown';
import { useTableSort, sortArrow } from '../hooks/useTableSort';

const PATIENT_SORT_COLS = [
  { key: 'id', label: 'ID' },
  { key: '_name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: '_last_visit_ms', label: 'Last Visit' },
  { key: 'sms_consent', label: 'Consent' },
  { key: null, label: 'Tags' },
  { key: '_status', label: 'Status' },
  { key: null, label: 'Actions' },
];

function Patients({ practiceId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  // GH-83 follow-up: distinguish initial mount from subsequent searches.
  // The full-page "Loading patients…" replacement was unmounting the search
  // input on every debounced keystroke, killing focus. Now `loading` is set
  // exactly once on initial mount; `searching` powers a tiny inline spinner
  // for re-fetches and the input stays mounted.
  const [searching, setSearching] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientTags, setPatientTags] = useState({});
  const [tagFilters, setTagFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPatients, setTotalPatients] = useState(0);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    last_visit_date: '',
    practice_id: practiceId || 1,
    nh_patient_id: '',
    push_to_athena: false,
  });

  useEffect(() => {
    setSelectedPatient(null); // Clear stale patient data on practice switch (IDOR fix)
    loadPatients({ isInitial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, practice_id: practiceId || 1 }));
  }, [practiceId]);

  // Deep link from the messaging rail: ?patient=<id> opens that patient's detail panel once, then clears the param.
  useEffect(() => {
    const id = parseInt(searchParams.get('patient') || '', 10);
    if (!id || !practiceId) return;
    let cancelled = false;
    api.getPatientDetail(practiceId, id)
      .then(r => { if (!cancelled && r?.data) setSelectedPatient(r.data); })
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete('patient');
        setSearchParams(next, { replace: true });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, searchParams.get('patient')]);

  // GH-83: Patient Directory search was a client-side filter over the
  // currently-loaded page, so anyone past the first PAGE_SIZE was invisible.
  // Re-fetch from the backend (which supports ?search=) when the query
  // changes. Debounce 250ms to avoid hammering the server on each keystroke.
  useEffect(() => {
    if (!practiceId) return;
    const handle = setTimeout(() => { loadPatients(); }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const PAGE_SIZE = 100;

  const loadPatients = async ({ isInitial = false } = {}) => {
    try {
      if (isInitial) setLoading(true);
      else setSearching(true);
      const response = await api.getPatients(practiceId, {
        limit: PAGE_SIZE,
        offset: 0,
        search: searchQuery || undefined,
      });
      setPatients(response.data || []);
      setTotalPatients(response.pagination?.total ?? (response.data || []).length);
      setHasMore((response.data || []).length >= PAGE_SIZE);
      // Batch-fetch tags for all loaded patients
      if (response.data?.length > 0 && practiceId) {
        const ids = response.data.map(p => p.id);
        const tagRes = await api.batchGetTags(practiceId, 'patient', ids);
        if (tagRes.success) setPatientTags(tagRes.data || {});
      }
    } catch (err) {
      console.error('Error loading patients:', err);
    } finally {
      if (isInitial) setLoading(false);
      else setSearching(false);
    }
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const response = await api.getPatients(practiceId, {
        limit: PAGE_SIZE,
        offset: patients.length,
        search: searchQuery || undefined,
      });
      const newPatients = response.data || [];
      setPatients(prev => [...prev, ...newPatients]);
      setHasMore(newPatients.length >= PAGE_SIZE);
      // Fetch tags for new patients
      if (newPatients.length > 0 && practiceId) {
        const ids = newPatients.map(p => p.id);
        const tagRes = await api.batchGetTags(practiceId, 'patient', ids);
        if (tagRes.success) setPatientTags(prev => ({ ...prev, ...(tagRes.data || {}) }));
      }
    } catch (err) {
      console.error('Error loading more patients:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // GH-51: backend now also pushes new patients to athena via Bridge.
      // Capture athena_warning from the response so we can surface it (e.g.
      // "Athena Go-Live approval is pending — change saved locally only").
      const createRes = await api.createPatient({ ...formData, practice_id: practiceId || formData.practice_id });

      let athenaMsg = '';
      if (createRes?.athena_warning) {
        athenaMsg = ` ${createRes.athena_warning}`;
      } else if (createRes?.data?.athena_patient_id) {
        athenaMsg = ` Pushed to athena (EHRID=${createRes.data.athena_patient_id}).`;
      }
      // Legacy explicit-push checkbox stays as a manual override but the new
      // auto-flow makes it optional — only run it if the auto-push didn't
      // already produce an athena_patient_id.
      if (formData.push_to_athena && !createRes?.data?.athena_patient_id) {
        try {
          const resp = await api.post(`/practices/${practiceId}/ehr/create-patient`, {
            firstName: formData.first_name,
            lastName: formData.last_name,
            dob: formData.date_of_birth || undefined,
            phone: formData.phone || undefined,
            email: formData.email || undefined,
          });
          const athenaId = resp?.data?.Identifiers?.find?.(i => i?.IDType === 'EHRID')?.ID
            || resp?.data?.athenaPatientId
            || resp?.data?.patientId;
          athenaMsg = athenaId ? ` Pushed to Athena (EHRID=${athenaId}).` : ' Pushed to Athena.';
        } catch (athenaErr) {
          athenaMsg = ` Athena push failed: ${athenaErr.response?.data?.error || athenaErr.message}`;
        }
      }

      setShowForm(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        last_visit_date: '',
        practice_id: practiceId || 1,
        nh_patient_id: '',
        push_to_athena: false,
      });
      await loadPatients();
      alert('Patient created successfully!' + athenaMsg);
    } catch (err) {
      alert(`Error creating patient: ${err.message}`);
    }
  };

  const handleOptOut = async (patientId) => {
    if (!window.confirm('Are you sure you want to opt out this patient?')) {
      return;
    }

    try {
      await api.optOutPatient(patientId, practiceId);
      await loadPatients();
      alert('Patient opted out successfully');
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleOptIn = async (patientId) => {
    if (!window.confirm('Are you sure you want to opt this patient back in?')) {
      return;
    }

    try {
      await api.optInPatient(patientId, practiceId);
      await loadPatients();
      alert('Patient opted back in successfully');
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  // Filter patients by search query and tags
  const filteredPatients = patients.filter(p => {
    // Tag filter
    if (tagFilters.length > 0) {
      const pTags = patientTags[p.id] || [];
      if (!tagFilters.some(filterId => pTags.some(t => t.tag_id === filterId))) return false;
    }
    // Text search across multiple fields (comma-separated terms use AND logic)
    if (searchQuery) {
      const terms = searchQuery.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      const searchable = `${p.first_name || ''} ${p.last_name || ''} ${p.phone || ''} ${p.mobile_phone || ''} ${p.email || ''} ${p.athena_patient_id || ''} ${p.date_of_birth || ''} ${p.city || ''} ${p.zip_code || ''}`.toLowerCase();
      if (!terms.every(term => searchable.includes(term))) return false;
    }
    return true;
  });

  const sortablePatients = filteredPatients.map(p => ({
    ...p,
    _name: `${p.first_name || ''} ${p.last_name || ''}`.trim().toLowerCase(),
    _last_visit_ms: p.last_visit_date ? new Date(p.last_visit_date).getTime() : null,
    _status: p.opted_out ? 'opted out' : 'active',
  }));
  const { sortedRows: sortedPatients, sortKey, sortDir, handleSort } = useTableSort(sortablePatients);

  const isCompressed = !!selectedPatient;

  if (loading) {
    return <div className="loading">Loading patients...</div>;
  }

  return (
    <div style={layoutStyles.container}>
      {/* Left Pane — Patient List */}
      <div style={{
        ...layoutStyles.listPane,
        width: isCompressed ? '30%' : '100%',
        minWidth: isCompressed ? '280px' : undefined,
      }}>
        {/* Header + Add Form */}
        <div style={{ padding: isCompressed ? '12px' : '24px 32px', margin: isCompressed ? '0 0 8px 0' : undefined }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{
              fontSize: isCompressed ? '16px' : '18px', margin: 0, fontWeight: 700,
              color: '#0f172a', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px',
            }}>
              Patients {isCompressed ? `(${filteredPatients.length})` : ''}
            </h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <input
                  type="text"
                  placeholder="Search (comma-separate to combine, e.g. Smith, 212)"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: isCompressed ? 160 : 260, color: '#334155', backgroundColor: '#fff' }}
                />
                {searching && (
                  <span style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 11, color: '#94a3b8', pointerEvents: 'none'
                  }}>…</span>
                )}
              </div>
              {searchQuery && (
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8' }} onClick={() => setSearchQuery('')}>&times;</button>
              )}
              {practiceId && (
                <TagFilterDropdown
                  practiceId={practiceId}
                  entityType="patient"
                  selected={tagFilters}
                  onChange={setTagFilters}
                />
              )}
              {!isCompressed && (
                <button
                  onClick={() => setShowForm(!showForm)}
                  style={{
                    padding: '8px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none',
                    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
                  }}
                >
                  {showForm ? 'Cancel' : 'Add New Patient'}
                </button>
              )}
            </div>
          </div>

          {showForm && !isCompressed && (
            <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label>First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>Last Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    placeholder="555-0100"
                  />
                </div>
                <div>
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>Last Visit Date <Tooltip text="Optional — used to determine eligibility for reactivation campaigns" /></label>
                  <input
                    type="date"
                    value={formData.last_visit_date}
                    onChange={(e) => setFormData({ ...formData, last_visit_date: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
                <div>
                  <label>NH Patient ID <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>(optional)</span></label>
                  <input
                    type="text"
                    value={formData.nh_patient_id}
                    onChange={(e) => setFormData({ ...formData, nh_patient_id: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    placeholder="Newton Health internal ID"
                  />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 15, fontSize: 13, color: '#334155' }}>
                <input
                  type="checkbox"
                  checked={!!formData.push_to_athena}
                  onChange={(e) => setFormData({ ...formData, push_to_athena: e.target.checked })}
                />
                Also create this patient in Athena (requires practice with EHR enabled)
              </label>
              <button type="submit" style={{
                marginTop: '15px', padding: '8px 20px', backgroundColor: '#2563eb', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}>
                Create Patient
              </button>
            </form>
          )}
        </div>

        {/* Patient List */}
        {isCompressed ? (
          /* Compressed mode — compact clickable rows */
          <div style={layoutStyles.compactList}>
            {filteredPatients.map((patient) => {
              const isSelected = selectedPatient?.id === patient.id;
              return (
                <div
                  key={patient.id}
                  onClick={() => setSelectedPatient(patient)}
                  style={{
                    ...layoutStyles.compactRow,
                    background: isSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
                    borderLeftColor: isSelected ? '#2563eb' : 'transparent',
                  }}
                >
                  <div style={layoutStyles.compactAvatar}>
                    {(patient.first_name || '?')[0]}{(patient.last_name || '?')[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={layoutStyles.compactName}>
                      {patient.first_name} {patient.last_name}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 2 }}>
                      {(patientTags[patient.id] || []).slice(0, 3).map(t => (
                        <TagBadge key={t.tag_id} name={t.name} color={t.color} size="small" />
                      ))}
                    </div>
                  </div>
                  {patient.opted_out ? (
                    <span style={{ ...layoutStyles.compactBadge, background: 'rgba(220,38,38,0.07)', color: '#dc2626' }}>Out</span>
                  ) : (
                    <span style={{ ...layoutStyles.compactBadge, background: 'rgba(22,163,74,0.08)', color: '#16a34a' }}>Active</span>
                  )}
                </div>
              );
            })}
            {filteredPatients.length === 0 && (
              <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                No patients found.
              </div>
            )}
          </div>
        ) : (
          /* Full mode — styled table */
          <div style={{ padding: '0 32px 24px' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                  Patient List ({filteredPatients.length < totalPatients
                    ? `${filteredPatients.length} of ${totalPatients}`
                    : totalPatients})
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {PATIENT_SORT_COLS.map((c, i) => (
                        <th key={c.label} onClick={c.key ? () => handleSort(c.key) : undefined} style={{
                          padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0',
                          fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase',
                          letterSpacing: 1.2, whiteSpace: 'nowrap', backgroundColor: '#f8fafc',
                          cursor: c.key ? 'pointer' : 'default', userSelect: 'none',
                        }}>
                          {c.label}{c.key ? sortArrow(c.key, sortKey, sortDir) : ''}
                          {i === 4 && <Tooltip text="Most recent date this patient was seen" />}
                          {i === 5 && <Tooltip text="SMS and Email consent status from Athena EHR" />}
                          {i === 7 && <Tooltip text="Active: Eligible for campaigns. Opted Out: Not receiving messages" />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPatients.length > 0 ? (
                      sortedPatients.map((patient) => (
                        // Opening a patient used to be a click handler on the row and
                        // nothing else: no role, no tab stop, no keyboard path. Anyone
                        // working without a mouse could not open a patient record at all.
                        // The name is now a real button, which also gives screen readers
                        // something to announce. The row keeps its click for pointer
                        // users, and the button stops the event so it fires once.
                        <tr
                          key={patient.id}
                          onClick={() => setSelectedPatient(patient)}
                          style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' }}>{patient.id}</td>
                          <td style={{ padding: '12px 16px', color: '#0f172a', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedPatient(patient); }}
                              aria-label={`Open patient ${patient.first_name} ${patient.last_name}`}
                              style={{
                                background: 'none', border: 'none', padding: 0, margin: 0,
                                font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left',
                              }}
                            >
                              {patient.first_name} {patient.last_name}
                            </button>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#334155' }}>{patient.email || '—'}</td>
                          <td style={{ padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' }}>{patient.phone}</td>
                          <td style={{ padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' }}>
                            {patient.last_visit_date
                              ? new Date(patient.last_visit_date).toLocaleDateString()
                              : '—'}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              <span style={{
                                padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                background: patient.sms_consent === 'OPT_IN' ? 'rgba(22,163,74,0.08)' : patient.sms_consent === 'OPT_OUT' ? 'rgba(220,38,38,0.07)' : '#f1f5f9',
                                color: patient.sms_consent === 'OPT_IN' ? '#16a34a' : patient.sms_consent === 'OPT_OUT' ? '#dc2626' : '#94a3b8',
                              }}>
                                SMS {patient.sms_consent === 'OPT_IN' ? 'Y' : patient.sms_consent === 'OPT_OUT' ? 'N' : '?'}
                              </span>
                              <span style={{
                                padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                background: patient.email_consent === 'OPT_IN' ? 'rgba(22,163,74,0.08)' : patient.email_consent === 'OPT_OUT' ? 'rgba(220,38,38,0.07)' : '#f1f5f9',
                                color: patient.email_consent === 'OPT_IN' ? '#16a34a' : patient.email_consent === 'OPT_OUT' ? '#dc2626' : '#94a3b8',
                              }}>
                                Email {patient.email_consent === 'OPT_IN' ? 'Y' : patient.email_consent === 'OPT_OUT' ? 'N' : '?'}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                              {(patientTags[patient.id] || []).map(t => (
                                <TagBadge key={t.tag_id} name={t.name} color={t.color} />
                              ))}
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {patient.opted_out ? (
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', background: 'rgba(220,38,38,0.07)', color: '#dc2626' }}>Opted Out</span>
                            ) : (
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', background: 'rgba(22,163,74,0.08)', color: '#16a34a' }}>Active</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {patient.opted_out ? (
                              <button
                                style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, color: '#2563eb', background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 8, cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); handleOptIn(patient.id); }}
                              >
                                Opt In
                              </button>
                            ) : (
                              <button
                                style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); handleOptOut(patient.id); }}
                              >
                                Opt Out
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: 14 }}>
                          No patients found. Add a patient to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Load More */}
        {hasMore && !searchQuery && (
          <div style={{ padding: '16px 32px', textAlign: 'center' }}>
            <button
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                padding: '10px 24px', fontSize: '13px', fontWeight: 600,
                color: '#2563eb', background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.2)',
                borderRadius: '8px', cursor: loadingMore ? 'not-allowed' : 'pointer', opacity: loadingMore ? 0.6 : 1,
              }}
            >
              {loadingMore ? 'Loading...' : `Load More (showing ${patients.length})`}
            </button>
          </div>
        )}
      </div>

      {/* Right Pane — Detail Panel */}
      {selectedPatient && (
        <div style={layoutStyles.detailPane}>
          <PatientDetailPanel
            patient={selectedPatient}
            practiceId={practiceId}
            onClose={() => setSelectedPatient(null)}
            onPatientUpdated={(updated) => {
              loadPatients();
              // Merge rather than replace: list rows carry fields the update
              // response does not, and dropping them blanks the other tabs.
              if (updated) setSelectedPatient(prev => ({ ...prev, ...updated }));
            }}
          />
        </div>
      )}
    </div>
  );
}

const layoutStyles = {
  container: {
    display: 'flex',
    height: 'calc(100vh - 64px)',
    overflow: 'hidden',
    background: '#f8fafc',
  },
  listPane: {
    transition: 'width 0.3s ease',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '16px',
    flexShrink: 0,
    background: '#ffffff',
    borderRight: '1px solid #e2e8f0',
  },
  detailPane: {
    width: '70%',
    flexShrink: 0,
    borderLeft: '1px solid #e2e8f0',
    background: '#f8fafc',
    overflowY: 'auto',
  },
  compactList: {
    overflow: 'hidden',
    padding: '0 4px',
  },
  compactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '11px 14px',
    cursor: 'pointer',
    borderRadius: '10px',
    borderLeft: '3px solid transparent',
    transition: 'all 0.2s ease',
    marginBottom: '3px',
  },
  compactAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'rgba(37,99,235,0.08)',
    color: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    flexShrink: 0,
    fontFamily: "'JetBrains Mono', monospace",
  },
  compactName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#0f172a',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  compactMeta: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '2px',
  },
  compactBadge: {
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    flexShrink: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
};

export default Patients;
